import { useEffect, useState } from "react";
import { Download, Share, PlusSquare, Smartphone } from "lucide-react";

const isInstalled = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

/** Mandatory Add-to-Home-Screen gate shown during registration. Blocks the page
 * until the app is installed (Android verified via appinstalled) or the user
 * confirms they added it (iOS/desktop — no install API exists there). */
export default function InstallGate() {
  const [passed, setPassed] = useState(() => isInstalled() || localStorage.getItem("icaard_a2hs_ok") === "1");
  const [deferred, setDeferred] = useState(null);
  const [ack, setAck] = useState(false);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  useEffect(() => {
    if (passed) return;
    const h = (e) => { e.preventDefault(); setDeferred(e); };
    const done = () => { localStorage.setItem("icaard_a2hs_ok", "1"); setPassed(true); };
    window.addEventListener("beforeinstallprompt", h);
    window.addEventListener("appinstalled", done);
    return () => { window.removeEventListener("beforeinstallprompt", h); window.removeEventListener("appinstalled", done); };
  }, [passed]);

  if (passed) return null;

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") { localStorage.setItem("icaard_a2hs_ok", "1"); setPassed(true); }
  };
  const confirm = () => {
    if (!ack) return;
    localStorage.setItem("icaard_a2hs_ok", "1");
    setPassed(true);
  };

  return (
    <div className="fixed inset-0 z-[120] bg-[#09090F]/97 backdrop-blur flex items-center justify-center p-5" data-testid="install-gate">
      <div className="w-full max-w-sm bg-[#13131E] border border-[#26263A] rounded-2xl p-5 space-y-4 text-white">
        <div className="flex items-center gap-2 text-[#22D3EE]"><Smartphone size={18} />
          <span className="text-[10px] tracking-[0.3em] uppercase font-bold">Required step</span>
        </div>
        <h2 className="font-display font-black text-xl">Add iCaard to your home screen</h2>
        <p className="text-xs text-[#9CA3D4]">To continue registration you must add iCaard to your phone's home screen. that's where your digital card, wallet and alerts live.</p>
        {deferred ? (
          <button onClick={install} data-testid="install-gate-install-btn"
                  className="w-full py-3.5 bg-[#22D3EE] text-black rounded-xl font-black text-sm flex items-center justify-center gap-2">
            <Download size={16} /> ADD TO HOME SCREEN NOW
          </button>
        ) : (
          <div className="rounded-xl bg-[#09090F] border border-[#26263A] p-3.5 text-xs space-y-2" data-testid="install-gate-steps">
            {isIOS ? (
              <>
                <div className="font-bold text-[#22D3EE]">On iPhone / iPad:</div>
                <div className="flex items-center gap-2">1. Tap the <Share size={13} className="text-[#22D3EE]" /> <b>Share</b> button in Safari</div>
                <div className="flex items-center gap-2">2. Scroll and tap <PlusSquare size={13} className="text-[#22D3EE]" /> <b>Add to Home Screen</b></div>
                <div>3. Tap <b>Add</b>. the iCaard icon appears on your home screen</div>
              </>
            ) : (
              <>
                <div className="font-bold text-[#22D3EE]">In your browser:</div>
                <div>1. Open the browser menu (⋮ or share icon)</div>
                <div>2. Tap <b>Install app</b> or <b>Add to Home screen</b></div>
                <div>3. Confirm. the iCaard icon appears on your home screen</div>
              </>
            )}
          </div>
        )}
        <label className="flex items-start gap-2.5 text-xs cursor-pointer">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                 data-testid="install-gate-ack" className="mt-0.5 accent-[#22D3EE]" />
          <span>I have added iCaard to my home screen</span>
        </label>
        <button onClick={confirm} disabled={!ack} data-testid="install-gate-continue"
                className="w-full py-3 rounded-xl font-black text-sm bg-[#10B981] text-black disabled:opacity-30">
          Continue registration →
        </button>
      </div>
    </div>
  );
}
